import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, CheckCircle, Eye } from "lucide-react";

const CATS = [
  { key: "C", label: "Export (C)" },
  { key: "D", label: "Domestic (D)" },
  { key: "K", label: "Import (K)" },
];

const ImportLegacyPage = () => {
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [category, setCategory] = useState("C");
  const API_URL = "/api";

  const handleUpload = async (dryRun) => {
    if (!file) return toast.error("Pilih file Excel dulu.");
    setIsLoading(true);
    setPreview(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", dryRun ? "true" : "false");
      fd.append("category", category);
      const res = await axios.post(`${API_URL}/invoices/import-legacy`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (dryRun) {
        setPreview(res.data);
        toast.success(`Preview: ${res.data.total_awb} AWB dari ${res.data.total_invoices} invoice.`);
      } else {
        setResult(res.data);
        toast.success(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal proses file.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6 w-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Import Invoice {CATS.find(c => c.key === category).label} – Sistem Lama</h2>
        <p className="text-gray-500 text-sm mt-1">Upload file Excel dari sistem lama (2 sheet: Summary + Detail AWB). AWB duplikat dilewati otomatis.</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
        <div className="mb-5">
          <p className="text-sm font-semibold text-gray-600 mb-2">Jenis Invoice</p>
          <div className="flex gap-2">
            {CATS.map(c => (
              <button key={c.key} type="button"
                onClick={() => { setCategory(c.key); setPreview(null); setResult(null); }}
                className={`px-4 py-2 rounded-xl text-sm font-semibold border transition ${category === c.key ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <h3 className="font-bold text-gray-700 mb-4">Upload File Excel</h3>
        <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 transition cursor-pointer"
          onClick={() => document.getElementById("legacy-file").click()}>
          <Upload className="mx-auto text-gray-400 mb-2" size={32} />
          <p className="text-gray-500 text-sm">{file ? file.name : "Klik atau drag file Excel (.xlsx / .xls)"}</p>
          <input id="legacy-file" type="file" accept=".xlsx,.xls" className="hidden"
            onChange={(e) => { setFile(e.target.files[0]); setPreview(null); setResult(null); }} />
        </div>
        {file && (
          <div className="flex gap-3 mt-4">
            <button onClick={() => handleUpload(true)} disabled={isLoading}
              className="flex items-center gap-2 px-5 py-2.5 border border-blue-600 text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition disabled:opacity-50">
              <Eye size={16} /> {isLoading ? "Memproses..." : "Preview Data"}
            </button>
            <button onClick={() => { if (!window.confirm(`Import invoice ${category} sekarang? AWB duplikat dilewati.`)) return; handleUpload(false); }}
              disabled={isLoading}
              className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition disabled:opacity-50">
              <CheckCircle size={16} /> {isLoading ? "Mengimport..." : "Import Sekarang"}
            </button>
          </div>
        )}
      </div>

      {result && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-5 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="text-green-600" size={20} />
            <span className="font-bold text-green-800">Import Berhasil</span>
          </div>
          <p className="text-green-700">{result.message}</p>
          <div className="flex gap-6 mt-3 text-sm text-green-700">
            <span>Diimport: <b>{result.inserted}</b> AWB</span>
            <span>Dilewati duplikat: <b>{result.skipped}</b></span>
            <span>Invoice Summary: <b>{result.sum_inserted}</b></span>
          </div>
        </div>
      )}

      {preview && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <h3 className="font-bold text-gray-700 mb-4">
            Preview — {preview.total_awb} AWB dari {preview.total_invoices} Invoice
            {preview.total_awb > 100 && <span className="text-xs text-gray-400 ml-2">(max 100 baris ditampilkan)</span>}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-gray-800 text-white">
                  {["Invoice No","AWB","Tgl AWB","Customer","Origin","Dest","Service","Weight(kg)","Base Price","FSC %","Inv Date","Due Date"].map(h => (
                    <th key={h} className="px-3 py-2 text-left whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.preview.map((r, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="px-3 py-2 font-mono text-blue-600 whitespace-nowrap">{r.invoice_number}</td>
                    <td className="px-3 py-2 font-mono whitespace-nowrap">{r.awb}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.date || "-"}</td>
                    <td className="px-3 py-2">{r.customer_name}</td>
                    <td className="px-3 py-2">{r.origin}</td>
                    <td className="px-3 py-2">{r.destination}</td>
                    <td className="px-3 py-2">{r.service_type}</td>
                    <td className="px-3 py-2 text-right">{Number(r.weight_kg).toFixed(3)}</td>
                    <td className="px-3 py-2 text-right">{Number(r.base_price).toLocaleString("id-ID")}</td>
                    <td className="px-3 py-2 text-right">{r.fsc_percent > 0 ? `${r.fsc_percent}%` : "-"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.invoice_date || "-"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.due_date || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportLegacyPage;
